import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import { Iconify } from 'src/components/iconify';
import { AppVersionFormValues, Locale } from './types';

const LOCALES: { value: Locale; label: string }[] = [
  { value: 'ZH_HANS', label: '中文' },
  { value: 'EN', label: 'English' },
];

function LocaleTabContent({ localeIndex }: { localeIndex: number }) {
  const { control, register } = useFormContext<AppVersionFormValues>();
  const translate = useTranslate();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `locales.${localeIndex}.releaseNotes` as any,
  });

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Controller
        name={`locales.${localeIndex}.title`}
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={translate('module.appVersionManagement.form.title')}
            fullWidth
            size="small"
            placeholder={translate('module.appVersionManagement.form.title')}
            required
            error={!!fieldState.error}
          />
        )}
      />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {translate('module.appVersionManagement.form.releaseNotes')}{' '}
          <span style={{ fontStyle: 'italic' }}>
            {translate('module.appVersionManagement.form.releaseNotesDesc')}
          </span>
        </Typography>
        <Stack spacing={1}>
          {fields.map((f, i) => (
            <Stack key={f.id} direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>
                {i + 1}.
              </Typography>
              <TextField
                {...register(`locales.${localeIndex}.releaseNotes.${i}` as any)}
                placeholder={translate('module.appVersionManagement.form.noteText')}
                size="small"
                fullWidth
              />
              <IconButton size="small" onClick={() => remove(i)}>
                <Iconify icon="mdi:trash-can-outline" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Box
          component="button"
          type="button"
          onClick={() => append('')}
          sx={{
            mt: 1,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 14,
            p: 0,
          }}
        >
          {translate('module.appVersionManagement.form.addItem')}
        </Box>
      </Box>

      <Controller
        name={`locales.${localeIndex}.button`}
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={translate('module.appVersionManagement.form.primaryButton')}
            fullWidth
            size="small"
            placeholder={translate('module.appVersionManagement.form.primaryButton')}
            required
            error={!!fieldState.error}
          />
        )}
      />
    </Stack>
  );
}

interface DetailFormProps {
  activeByPlatform?: Record<string, string>;
}

export default function DetailForm({ activeByPlatform }: DetailFormProps) {
  const { control, setValue } = useFormContext<AppVersionFormValues>();
  const { errors, submitCount } = useFormState({ control });
  const translate = useTranslate();
  const [localeTab, setLocaleTab] = useState(0);

  const versionCode = useWatch({ control, name: 'versionCode' });
  const forceUpdate = useWatch({ control, name: 'forceUpdate' });
  const active = useWatch({ control, name: 'active' });
  const platform = useWatch({ control, name: 'platform' });

  const activeVersionCode = activeByPlatform?.[platform];

  useEffect(() => {
    if (forceUpdate) {
      setValue('minVersion', versionCode?.trim() ?? '', { shouldDirty: true });
    }
  }, [forceUpdate, versionCode, setValue]);

  useEffect(() => {
    if (submitCount === 0 || !errors.locales) return;
    const localesErrors = errors.locales as any;
    for (let i = 0; i < LOCALES.length; i += 1) {
      if (localesErrors[i] != null) {
        setLocaleTab(i);
        return;
      }
    }
  }, [submitCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const PLATFORM_OPTIONS = [
    // { value: 'ALL', label: translate('module.appVersionManagement.platform.ALL') },
    { value: 'IOS', label: translate('module.appVersionManagement.platform.IOS') },
    { value: 'ANDROID', label: translate('module.appVersionManagement.platform.ANDROID') },
  ];

  const FREQUENCY_OPTIONS = [
    {
      value: 'EVERY_LAUNCH',
      label: translate('module.appVersionManagement.frequency.EVERY_LAUNCH'),
    },
    {
      value: 'ONCE_PER_DAY',
      label: translate('module.appVersionManagement.frequency.ONCE_PER_DAY'),
    },
    { value: 'ONCE_ONLY', label: translate('module.appVersionManagement.frequency.ONCE_ONLY') },
  ];

  return (
    <Stack spacing={3}>
      {activeVersionCode && active && (
        <Alert severity="warning" sx={{ mb: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {translate('module.appVersionManagement.activeWarning')}
          </Typography>
          <Typography variant="body2">
            {translate('module.appVersionManagement.savingWillDisable', {
              versionCode: activeVersionCode,
            })}
          </Typography>
        </Alert>
      )}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Controller
                name="versionCode"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const val = raw.startsWith('v') ? raw : `v${raw}`;
                      field.onChange(val);
                    }}
                    label={translate('module.appVersionManagement.form.version')}
                    fullWidth
                    size="small"
                    placeholder="e.g. v1.4.2"
                    helperText={fieldState.error?.message || 'e.g. v1.4.2'}
                    required
                    error={!!fieldState.error}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="platform"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={translate('module.appVersionManagement.form.platform')}
                    fullWidth
                    size="small"
                    SelectProps={{ native: true }}
                  >
                    {PLATFORM_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="minVersion"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={forceUpdate ? (versionCode?.trim() ?? '') : field.value}
                    label={translate('module.appVersionManagement.form.minVersion')}
                    fullWidth
                    onChange={(e) => {
                      const raw = e.target.value;
                      const val = raw.startsWith('v') ? raw : `v${raw}`;
                      field.onChange(val);
                    }}
                    size="small"
                    disabled={forceUpdate}
                    placeholder={translate(
                      'module.appVersionManagement.form.minVersionPlaceholder'
                    )}
                    helperText={translate('module.appVersionManagement.form.minVersionHelper')}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="showFrequency"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={translate('module.appVersionManagement.form.frequency')}
                    fullWidth
                    size="small"
                    SelectProps={{ native: true }}
                  >
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ pt: 0.5 }}>
                <Controller
                  name="forceUpdate"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value ?? false}
                      onChange={(_, v) => {
                        field.onChange(v);
                        if (v) {
                          setValue('minVersion', versionCode?.trim() ?? '', { shouldDirty: true });
                        } else {
                          setValue('minVersion', '', { shouldDirty: true });
                        }
                      }}
                      size="small"
                    />
                  )}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {translate('module.appVersionManagement.form.forceUpdate')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('module.appVersionManagement.form.forceUpdateDesc')}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ pt: 0.5 }}>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value ?? false}
                      onChange={(_, v) => field.onChange(v)}
                      size="small"
                    />
                  )}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {translate('module.appVersionManagement.form.enabled')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('module.appVersionManagement.form.enabledDesc')}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {translate('module.appVersionManagement.form.dialogContent')}
          </Typography>
          <Tabs value={localeTab} onChange={(_, v) => setLocaleTab(v)}>
            {LOCALES.map((loc, i) => (
              <Tab key={loc.value} label={loc.label} value={i} />
            ))}
          </Tabs>
          {LOCALES.map((loc, i) => (
            <Box key={loc.value} sx={{ display: localeTab === i ? 'block' : 'none' }}>
              <LocaleTabContent localeIndex={i} />
            </Box>
          ))}
        </CardContent>
      </Card>
    </Stack>
  );
}
